/**
 * useBrand — React access to the active brand.
 *
 * Re-renders downstream components when the brand changes (e.g. user
 * toggles `?brand=marketplace` in dev). Production navigations between
 * homesbyli.com and the marketplace are full page loads, so the value
 * is stable after mount — but the provider still listens for hashchange
 * + popstate so query-param flips during dev are picked up live.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { resolveBrandKey, BRAND_REGISTRY, siblingBrand } from './brandContext';

const BrandCtx = createContext(null);

export const BrandProvider = ({ children }) => {
  const [brandKey, setBrandKey] = useState(() => resolveBrandKey());

  // Re-resolve on URL changes — covers the dev `?brand=…` override
  // and any future client-side brand-switch buttons.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const refresh = () => {
      const next = resolveBrandKey();
      setBrandKey((prev) => (prev === next ? prev : next));
    };
    window.addEventListener('popstate', refresh);
    window.addEventListener('hashchange', refresh);
    return () => {
      window.removeEventListener('popstate', refresh);
      window.removeEventListener('hashchange', refresh);
    };
  }, []);

  const brand = BRAND_REGISTRY[brandKey];
  const sibling = siblingBrand(brandKey);

  // Helper: build an absolute cross-brand URL preserving path/search.
  // In production, hostname will be the sibling's real domain. In
  // preview/dev, fall back to the same origin with `?brand=…` so the
  // preview server still serves both brands without DNS work.
  //
  // `opts.ref` — attribution tag (e.g. "ucwonder-comingsoon") appended
  // as `?ref=…` on top of any brand override. Lets us measure which
  // UCWonder surface converts hosts / gift-card buyers best without
  // needing analytics tooling: the destination page reads
  // `URLSearchParams(location.search).get('ref')` and stashes it in
  // localStorage / the host-application submission.
  const crossBrandHref = (path = '/', opts = {}) => {
    if (!sibling) return path;
    const { ref } = opts;
    const isProdLike =
      typeof window !== 'undefined' &&
      sibling.hostnames?.length > 0 &&
      // Only redirect to the sibling's real hostname if THIS page is
      // actually being served from a registered hostname. Preview URLs
      // (emergentagent.com, localhost) keep using the query-param trick.
      brand.hostnames?.some((h) => window.location.hostname === h);

    let url;
    if (isProdLike) {
      url = `https://${sibling.hostnames[0]}${path}`;
    } else {
      const sep = path.includes('?') ? '&' : '?';
      url = `${path}${sep}brand=${sibling.key}`;
    }

    if (ref) {
      const refSep = url.includes('?') ? '&' : '?';
      url = `${url}${refSep}ref=${encodeURIComponent(ref)}`;
    }
    return url;
  };

  return (
    <BrandCtx.Provider value={{ brand, brandKey, sibling, crossBrandHref }}>
      {children}
    </BrandCtx.Provider>
  );
};

export const useBrand = () => {
  const ctx = useContext(BrandCtx);
  if (!ctx) {
    // Safe fallback so components that mount outside the provider
    // (e.g. design previews) don't crash.
    return {
      brand: BRAND_REGISTRY.stays,
      brandKey: 'stays',
      sibling: BRAND_REGISTRY.marketplace,
      crossBrandHref: (p = '/', opts = {}) => {
        const sep = p.includes('?') ? '&' : '?';
        let url = `${p}${sep}brand=marketplace`;
        if (opts.ref) url += `&ref=${encodeURIComponent(opts.ref)}`;
        return url;
      },
    };
  }
  return ctx;
};
